import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useAuth } from '@/lib/auth-store';
import { Card, CardContent } from '@/components/ui/card';
import { ToggleLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';

interface Props {
  feature: string;
  children: React.ReactNode;
}

/**
 * Gate a page behind a feature flag. Super-admins bypass. When the flag is
 * off we render an inline explanation + deep-link back to the feature-flags
 * page so the user (or their reseller/owner) can flip it on.
 */
export function FeatureGate({ feature, children }: Props) {
  const { isEnabled, isLoading, previewing } = useFeatureFlag();
  const user = useAuth((s) => s.user);
  // Super admin: never gated — UNLESS previewing an entered customer, where
  // we reflect that customer's real flags so a disabled feature actually
  // shows as disabled.
  if (user?.is_super_admin && !previewing) return <>{children}</>;
  // While loading, render nothing to avoid flash.
  if (isLoading) return null;

  if (!isEnabled(feature)) {
    return <NotEnabled feature={feature} />;
  }
  return <>{children}</>;
}

function NotEnabled({ feature }: { feature: string }) {
  const user = useAuth((s) => s.user);
  // 2026-08-06 (Sam): office_manager removed — customers cannot manage
  // their own flags. Only the platform owner and resellers get the
  // manage link; customer-tier roles are pointed at their provider.
  const canToggle = user?.is_super_admin || user?.role === 'reseller_owner';
  return (
    <div className="mx-auto max-w-xl pt-12">
      <Card className="border-dashed">
        <CardContent className="py-10 text-center space-y-4">
          <ToggleLeft className="h-10 w-10 mx-auto text-muted-foreground" strokeWidth={1.5} />
          <div>
            <p className="text-base font-medium">Feature not enabled</p>
            <p className="mt-1 text-sm text-muted-foreground">
              The <code className="font-mono text-xs">{feature}</code> feature isn't active for your account right now.
            </p>
          </div>
          {canToggle ? (
            <div>
              <Button asChild size="sm" variant="outline">
                <Link to="/settings/features">Manage feature flags</Link>
              </Button>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">
              Contact your service provider to have it turned on.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
